/**
 * Flight operations · the figures a person enters on the flight record:
 * actual times, loads, delay codes, the handler's committed off-block time,
 * and a flight the seasonal schedule does not have.
 *
 * Each form sends one entry to the platform, which checks it (the mock
 * checks it the same way, domain/flightEntries.ts) and refuses with the
 * reason in words; the reason is shown under the form. Times are typed as
 * the airport's local HH:MM on the flight's day. An entry that overwrites
 * what a movement message said says so.
 */
import { useState } from "react";
import { useAodbActuals, useAodbAdd, useAodbDelayCodes, useAodbLoads, useAodbTobt } from "../../api/queries";
import { Button, Card, Field, Input, Select } from "../../design/components";
import type { Movement } from "../../domain/aodb";
import { DELAY_CODES, delayCodeLabel } from "../../domain/delayCodes";
import {
  ACTUALS,
  LOADS,
  localDate,
  localHhmm,
  localToIso,
  sourceWords,
  type ActualKey,
  type LoadKey,
  type Provenance,
} from "../../domain/flightEntries";
import { t } from "../../i18n";
import { tx } from "../../i18n/computed";
import a from "./Aodb.module.css";

const errorText = (e: unknown) => tx(e instanceof Error ? e.message : String(e));

/** "entered by hand · A. Faisal", "from the MVT" — beside a figure, where the record says. */
export function SourceNote({ p, testId }: { p?: Provenance; testId?: string }) {
  const words = sourceWords(p?.source);
  if (!p || !words) return null;
  return (
    <span className={a.src} data-src={p.source} data-testid={testId}>
      {t(words)}
      {p.source === "manual" && p.by ? ` · ${p.by}` : ""}
    </span>
  );
}

function Saved({ replaced }: { replaced: { field: string; source: string }[] }) {
  return (
    <p className={a.result} data-outcome="applied" data-testid="entry-saved">
      <b>{t("Saved to the record.")}</b>{" "}
      {replaced.length > 0 &&
        t("It replaces what the {src} said.", {
          src: [...new Set(replaced.map((r) => r.source.toUpperCase()))].join(", "),
        })}
    </p>
  );
}

/* ── actual times ─────────────────────────────────────────────────────── */

const ACTUAL_LABEL: Record<ActualKey, string> = {
  off_block: "Off-block (local HH:MM)",
  takeoff: "Take-off (local HH:MM)",
  landing: "Landing (local HH:MM)",
  on_block: "On-block (local HH:MM)",
};

export function ActualsForm({ m }: { m: Movement }) {
  const save = useAodbActuals();
  const keys = (Object.keys(ACTUALS) as ActualKey[]).filter((k) => ACTUALS[k].direction === m.direction);
  const start = () => Object.fromEntries(keys.map((k) => [k, localHhmm(m.milestones?.[ACTUALS[k].milestone])]));
  const [v, setV] = useState<Record<string, string>>(start);
  const [note, setNote] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const [done, setDone] = useState<{ field: string; source: string }[] | null>(null);
  const changed = keys.filter((k) => v[k] && v[k] !== localHhmm(m.milestones?.[ACTUALS[k].milestone]));
  const submit = () => {
    setBad(null);
    setDone(null);
    const times: Partial<Record<ActualKey, string>> = {};
    for (const k of changed) {
      const at = localToIso(m.serviceDate, v[k], m.scheduledAt);
      if (!at) {
        setBad(t("{v} is not a time; type it as HH:MM, like 14:35.", { v: v[k] }));
        return;
      }
      times[k] = at;
    }
    save.mutate(
      { movementId: m.id, times, version: m.version, note: note || undefined },
      { onSuccess: (r) => (setDone(r.replaced), setNote("")), onError: (e) => setBad(errorText(e)) },
    );
  };
  return (
    <form
      className={a.form}
      data-testid="entry-actuals"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h3 className={a.h3}>{t("Actual times")}</h3>
      <div className={a.pair}>
        {keys.map((k) => (
          <Field key={k} label={ACTUAL_LABEL[k]}>
            <Input
              value={v[k] ?? ""}
              onChange={(e) => setV({ ...v, [k]: e.target.value })}
              placeholder="14:35"
              inputMode="numeric"
              data-testid={`actual-${k}`}
            />
          </Field>
        ))}
      </div>
      <Field label="Why entered by hand">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("no MVT came, the handler's log …")}
        />
      </Field>
      <Button type="submit" variant="primary" disabled={save.isPending || !changed.length} data-testid="actuals-save">
        {t("Save actual times")}
      </Button>
      {!changed.length && <p className={a.dim}>{t("Type a time to save it.")}</p>}
      {bad && (
        <p className={a.bad} role="alert" data-testid="actuals-refused">
          {bad}
        </p>
      )}
      {done && <Saved replaced={done} />}
    </form>
  );
}

/* ── loads ────────────────────────────────────────────────────────────── */

const LOAD_LABEL: Record<LoadKey, string> = {
  booked: "Booked",
  actual: "On board",
  transfer: "Transfer",
  bags: "Bags (pieces)",
  cargo_kg: "Cargo (kg)",
  mail_kg: "Mail (kg)",
};

export function LoadsForm({ m }: { m: Movement }) {
  const save = useAodbLoads();
  const keys = Object.keys(LOADS) as LoadKey[];
  const start = (): Record<string, string> => ({
    ...Object.fromEntries(keys.map((k) => [k, m.loads?.[k] != null ? String(m.loads[k]) : ""])),
    seats: m.capacity != null ? String(m.capacity) : "",
  });
  const [v, setV] = useState<Record<string, string>>(start);
  const [note, setNote] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const [done, setDone] = useState<{ field: string; source: string }[] | null>(null);
  const was = start();
  const changed = [...keys, "seats"].filter((k) => v[k].trim() !== was[k]);
  const num = (s: string) => (s.trim() === "" ? null : Number(s.trim()));
  const submit = () => {
    setBad(null);
    setDone(null);
    const loads: Record<string, number | null> = {};
    for (const k of keys) if (changed.includes(k)) loads[k] = num(v[k]);
    save.mutate(
      {
        movementId: m.id,
        loads,
        ...(changed.includes("seats") ? { capacity: { value: num(v.seats) } } : {}),
        version: m.version,
        note: note || undefined,
      },
      { onSuccess: (r) => (setDone(r.replaced), setNote("")), onError: (e) => setBad(errorText(e)) },
    );
  };
  return (
    <form
      className={a.form}
      data-testid="entry-loads"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h3 className={a.h3}>{t("Loads")}</h3>
      <div className={a.pair}>
        {keys.map((k) => (
          <Field key={k} label={LOAD_LABEL[k]}>
            <Input
              value={v[k]}
              onChange={(e) => setV({ ...v, [k]: e.target.value })}
              inputMode="numeric"
              data-testid={`load-${k}`}
            />
          </Field>
        ))}
        <Field label="Seats">
          <Input
            value={v.seats}
            onChange={(e) => setV({ ...v, seats: e.target.value })}
            inputMode="numeric"
            data-testid="load-seats"
          />
        </Field>
      </div>
      <Field label="Where the figures came from">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("the load sheet, the handler …")}
        />
      </Field>
      <Button type="submit" variant="primary" disabled={save.isPending || !changed.length} data-testid="loads-save">
        {t("Save loads")}
      </Button>
      {!changed.length && <p className={a.dim}>{t("Change a figure to save it. Clear one to remove it.")}</p>}
      {bad && (
        <p className={a.bad} role="alert" data-testid="loads-refused">
          {bad}
        </p>
      )}
      {done && <Saved replaced={done} />}
    </form>
  );
}

/* ── delay codes ──────────────────────────────────────────────────────── */

const CODE_LIST = Object.keys(DELAY_CODES);

export function DelayCodesForm({ m }: { m: Movement }) {
  const save = useAodbDelayCodes();
  const start = () =>
    m.delayCodes.length
      ? m.delayCodes.map((c) => ({ code: c.code, minutes: c.minutes != null ? String(c.minutes) : "" }))
      : [{ code: "", minutes: "" }];
  const [rows, setRows] = useState<{ code: string; minutes: string }[]>(start);
  const [note, setNote] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const [done, setDone] = useState<{ field: string; source: string }[] | null>(null);
  const late =
    m.actualAt && m.scheduledAt ? Math.round((Date.parse(m.actualAt) - Date.parse(m.scheduledAt)) / 60_000) : null;
  const total = rows.reduce((n, r) => n + (Number(r.minutes) || 0), 0);
  const set = (i: number, patch: Partial<{ code: string; minutes: string }>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = () => {
    setBad(null);
    setDone(null);
    const codes = rows
      .filter((r) => r.code.trim())
      .map((r) => ({ code: r.code.trim(), ...(r.minutes.trim() ? { minutes: Number(r.minutes.trim()) } : {}) }));
    save.mutate(
      { movementId: m.id, codes, version: m.version, note: note || undefined },
      { onSuccess: (r) => (setDone(r.replaced), setNote("")), onError: (e) => setBad(errorText(e)) },
    );
  };
  return (
    <form
      className={a.form}
      data-testid="entry-codes"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h3 className={a.h3}>{t("Delay codes")}</h3>
      {late !== null && (
        <p className={a.dim}>
          {late > 0 ? t("Flew {n} min late. The minutes should add up to that.", { n: late }) : t("Flew on time.")}
        </p>
      )}
      {rows.map((r, i) => (
        <div className={a.pair} key={i}>
          <Field label="IATA code">
            <Input
              value={r.code}
              onChange={(e) => set(i, { code: e.target.value })}
              list="iata-delay-codes"
              placeholder="93"
              data-testid={`code-${i}`}
            />
          </Field>
          <Field label="Minutes">
            <Input
              value={r.minutes}
              onChange={(e) => set(i, { minutes: e.target.value })}
              inputMode="numeric"
              data-testid={`minutes-${i}`}
            />
          </Field>
          {r.code && delayCodeLabel(r.code) && <span className={a.dim}>{t(delayCodeLabel(r.code)!)}</span>}
        </div>
      ))}
      <datalist id="iata-delay-codes">
        {CODE_LIST.map((c) => (
          <option key={c} value={c}>
            {t(DELAY_CODES[c])}
          </option>
        ))}
      </datalist>
      <div className={a.row}>
        <Button size="sm" onClick={() => setRows([...rows, { code: "", minutes: "" }])} disabled={rows.length >= 6}>
          {t("Add a code")}
        </Button>
        {total > 0 && <span className={a.dim}>{t("{n} min in all", { n: total })}</span>}
      </div>
      <Field label="Why">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("the airline's delay report …")} />
      </Field>
      <Button type="submit" variant="primary" disabled={save.isPending} data-testid="codes-save">
        {t("Save delay codes")}
      </Button>
      {bad && (
        <p className={a.bad} role="alert" data-testid="codes-refused">
          {bad}
        </p>
      )}
      {done && <Saved replaced={done} />}
    </form>
  );
}

/* ── the handler's committed off-block time ───────────────────────────── */

export function TobtForm({
  movementId,
  serviceDate,
  near,
  version,
  onDone,
}: {
  movementId: string;
  serviceDate: string;
  near?: string;
  version?: number;
  onDone?: () => void;
}) {
  const save = useAodbTobt();
  const [at, setAt] = useState("");
  const [who, setWho] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const submit = () => {
    setBad(null);
    const iso = localToIso(serviceDate, at, near);
    if (!iso) {
      setBad(t("{v} is not a time; type it as HH:MM, like 14:35.", { v: at }));
      return;
    }
    save.mutate(
      { movementId, tobt: iso, givenBy: who || undefined, version },
      { onSuccess: () => (setAt(""), onDone?.()), onError: (e) => setBad(errorText(e)) },
    );
  };
  return (
    <form
      className={a.form}
      data-testid="entry-tobt"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className={a.pair}>
        <Field label="Committed off-block (local HH:MM)">
          <Input value={at} onChange={(e) => setAt(e.target.value)} placeholder="14:35" data-testid="tobt-time" />
        </Field>
        <Field label="Given by">
          <Input value={who} onChange={(e) => setWho(e.target.value)} placeholder={t("the handler or airline")} />
        </Field>
      </div>
      <Button type="submit" variant="primary" size="sm" disabled={save.isPending || !at.trim()} data-testid="tobt-save">
        {t("Save committed time")}
      </Button>
      {bad && (
        <p className={a.bad} role="alert" data-testid="tobt-refused">
          {bad}
        </p>
      )}
    </form>
  );
}

/* ── a flight the schedule does not have ──────────────────────────────── */

export function NewMovementCard({ onClose, onAdded }: { onClose: () => void; onAdded?: (m: Movement) => void }) {
  const add = useAodbAdd();
  const today = localDate(Date.now());
  const [v, setV] = useState({
    direction: "arrival" as Movement["direction"],
    carrier: "",
    number: "",
    serviceDate: today,
    time: "",
    counterpartNode: "",
    classKey: "",
    registration: "",
    serviceType: "pax",
    capacity: "",
    note: "",
  });
  const [bad, setBad] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const set = (patch: Partial<typeof v>) => setV({ ...v, ...patch });
  const submit = () => {
    setBad(null);
    setDone(null);
    const at = localToIso(v.serviceDate, v.time);
    if (!at) {
      setBad(t("{v} is not a time; type it as HH:MM, like 14:35.", { v: v.time || "—" }));
      return;
    }
    add.mutate(
      {
        carrier: v.carrier,
        number: v.number,
        direction: v.direction,
        serviceDate: v.serviceDate,
        scheduledAt: at,
        counterpartNode: v.counterpartNode || undefined,
        classKey: v.classKey || undefined,
        registration: v.registration || undefined,
        serviceType: v.serviceType,
        capacity: v.capacity.trim() ? Number(v.capacity) : null,
        note: v.note || undefined,
      },
      {
        onSuccess: (m) => {
          setDone(t("{f} is on the record.", { f: `${m.carrier}${m.number}` }));
          onAdded?.(m);
        },
        onError: (e) => setBad(errorText(e)),
      },
    );
  };
  return (
    <Card
      title="Add a flight"
      sub="A flight that isn't on the schedule"
      testId="aodb-new"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
    >
      <form
        className={a.form}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className={a.pair}>
          <Field label="Direction">
            <Select
              value={v.direction}
              onChange={(e) => set({ direction: e.target.value as Movement["direction"] })}
              data-testid="new-direction"
            >
              <option value="arrival">{t("Arrival")}</option>
              <option value="departure">{t("Departure")}</option>
            </Select>
          </Field>
          <Field label="Airline code">
            <Input
              value={v.carrier}
              onChange={(e) => set({ carrier: e.target.value })}
              placeholder="SV"
              data-testid="new-carrier"
            />
          </Field>
          <Field label="Flight number">
            <Input
              value={v.number}
              onChange={(e) => set({ number: e.target.value })}
              placeholder="1483"
              data-testid="new-number"
            />
          </Field>
          <Field label="Day">
            <Input
              type="date"
              value={v.serviceDate}
              onChange={(e) => set({ serviceDate: e.target.value })}
              data-testid="new-day"
            />
          </Field>
          <Field label="Scheduled (local HH:MM)">
            <Input
              value={v.time}
              onChange={(e) => set({ time: e.target.value })}
              placeholder="14:35"
              data-testid="new-time"
            />
          </Field>
          <Field label={v.direction === "arrival" ? "From (airport code)" : "To (airport code)"}>
            <Input
              value={v.counterpartNode}
              onChange={(e) => set({ counterpartNode: e.target.value })}
              placeholder="JED"
              data-testid="new-node"
            />
          </Field>
          <Field label="Type">
            <Input value={v.classKey} onChange={(e) => set({ classKey: e.target.value })} placeholder="A320" />
          </Field>
          <Field label="Registration">
            <Input
              value={v.registration}
              onChange={(e) => set({ registration: e.target.value })}
              placeholder="HZ-AKA"
            />
          </Field>
          <Field label="Service">
            <Select value={v.serviceType} onChange={(e) => set({ serviceType: e.target.value })}>
              {(["pax", "cargo", "ferry", "positioning", "ga", "military", "other"] as const).map((x) => (
                <option key={x} value={x}>
                  {t(SERVICE_WORDS[x])}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Seats">
            <Input
              value={v.capacity}
              onChange={(e) => set({ capacity: e.target.value })}
              inputMode="numeric"
              data-testid="new-seats"
            />
          </Field>
        </div>
        <Field label="Why it isn't on the schedule">
          <Input
            value={v.note}
            onChange={(e) => set({ note: e.target.value })}
            placeholder={t("a charter, a diversion, an extra section")}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={add.isPending} data-testid="new-save">
          {t("Add to the record")}
        </Button>
        {bad && (
          <p className={a.bad} role="alert" data-testid="new-refused">
            {bad}
          </p>
        )}
        {done && (
          <p className={a.result} data-outcome="applied" data-testid="new-added">
            <b>{done}</b>
          </p>
        )}
      </form>
    </Card>
  );
}

const SERVICE_WORDS: Record<string, string> = {
  pax: "Passenger",
  cargo: "Cargo",
  ferry: "Ferry",
  positioning: "Positioning",
  ga: "General aviation",
  military: "Military",
  other: "Other",
};
